import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Package, ArrowLeft, Save, Search, Eye, EyeOff, Plus, Edit2, Trash2,
  X, Upload, Star, Store,
} from 'lucide-react';
import api from '../utils/api';
import { resolveImageUrl } from '../lib/imageUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============= TAB 1: PANASONIC PRODUCTS =============
const PanasonicProductsTab = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [editedPrices, setEditedPrices] = useState({});
  const [editedVisibility, setEditedVisibility] = useState({});

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/reseller/products');
      setProducts(response.data);
      const prices = {};
      const visibility = {};
      response.data.forEach(p => {
        prices[p.id] = p.b2c_price_gross !== null ? p.b2c_price_gross.toString().replace('.', ',') : '';
        visibility[p.id] = p.is_visible_b2c;
      });
      setEditedPrices(prices);
      setEditedVisibility(visibility);
    } catch (error) {
      toast.error('Fehler beim Laden der Produkte');
      if (error.response?.status === 404) {
        navigate('/my-shop');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (productId, value) => {
    const cleanValue = value.replace(/[^0-9,]/g, '');
    setEditedPrices(prev => ({ ...prev, [productId]: cleanValue }));
  };

  const handleVisibilityChange = (productId, visible) => {
    setEditedVisibility(prev => ({ ...prev, [productId]: visible }));
  };

  const handleSaveProduct = async (productId) => {
    setSaving(prev => ({ ...prev, [productId]: true }));
    try {
      const priceStr = editedPrices[productId];
      const b2cPrice = priceStr ? parseFloat(priceStr.replace(',', '.')) : null;
      await api.put(`/reseller/products/${productId}/settings`, {
        b2c_price_gross: b2cPrice,
        is_visible: editedVisibility[productId],
      });
      toast.success('Produkteinstellungen gespeichert!');
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(prev => ({ ...prev, [productId]: false }));
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '-';
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(price);
  };

  const calculateMargin = (b2bPrice, b2cPriceStr) => {
    if (!b2cPriceStr) return { absolute: null, percent: null };
    const b2cPrice = parseFloat(b2cPriceStr.replace(',', '.'));
    if (isNaN(b2cPrice) || b2cPrice <= 0) return { absolute: null, percent: null };
    return {
      absolute: b2cPrice - b2bPrice,
      percent: ((b2cPrice - b2bPrice) / b2bPrice) * 100,
    };
  };

  const filteredProducts = products.filter(p =>
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.sku?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="panasonic-products-tab">
      <div className="mb-4 text-sm text-gray-600">
        Zentrale Panasonic-Produkte. Einkaufspreis ist schreibgeschützt.
        Sie legen nur Ihren B2C-Verkaufspreis und die Sichtbarkeit fest.
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Produkt suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="ps-10"
            data-testid="search-input"
          />
        </div>
      </div>

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-start px-4 py-3 text-sm font-medium text-gray-600">Produkt</th>
                <th className="text-start px-4 py-3 text-sm font-medium text-gray-600">Art.-Nr.</th>
                <th className="text-end px-4 py-3 text-sm font-medium text-gray-600">B2B-EK (Netto) <span className="text-gray-400">🔒</span></th>
                <th className="text-end px-4 py-3 text-sm font-medium text-gray-600">B2C-VK (Brutto)</th>
                <th className="text-end px-4 py-3 text-sm font-medium text-gray-600">Spanne</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Sichtbar</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Aktion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredProducts.map(product => {
                const margin = calculateMargin(product.b2b_price, editedPrices[product.id]);
                return (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {product.image_url && (
                          <img src={resolveImageUrl(product.image_url)} alt={product.name} className="w-12 h-12 object-contain rounded bg-white border" loading="lazy" />
                        )}
                        <div>
                          <div className="font-medium">{product.name}</div>
                          {product.barcode && (
                            <div className="text-xs text-gray-500">Barcode: {product.barcode}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{product.sku || '-'}</td>
                    <td className="px-4 py-3 text-end font-medium text-gray-500" title="Einkaufspreis – nicht änderbar">
                      {formatPrice(product.b2b_price)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end">
                        <div className="relative w-28">
                          <Input
                            value={editedPrices[product.id] || ''}
                            onChange={(e) => handlePriceChange(product.id, e.target.value)}
                            placeholder="0,00"
                            className="text-end pe-8"
                            data-testid={`price-input-${product.id}`}
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-end">
                      {margin.absolute !== null ? (
                        <div>
                          <div className={`font-medium ${margin.absolute >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {margin.absolute >= 0 ? '+' : ''}{formatPrice(margin.absolute)}
                          </div>
                          <div className="text-xs text-gray-500">
                            ({margin.percent >= 0 ? '+' : ''}{margin.percent.toFixed(1)}%)
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleVisibilityChange(product.id, !editedVisibility[product.id])}
                        className={`p-2 rounded-full transition-colors ${
                          editedVisibility[product.id]
                            ? 'bg-green-100 text-green-600 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                        }`}
                        data-testid={`visibility-toggle-${product.id}`}
                      >
                        {editedVisibility[product.id] ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Button
                        size="sm"
                        onClick={() => handleSaveProduct(product.id)}
                        disabled={saving[product.id]}
                        data-testid={`save-btn-${product.id}`}
                      >
                        <Save className="h-3 w-3 me-1" />
                        {saving[product.id] ? '...' : 'Speichern'}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filteredProducts.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            {searchTerm ? 'Keine Produkte gefunden' : 'Keine Produkte verfügbar'}
          </div>
        )}
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">Hinweis</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Der <strong>B2B-EK</strong> ist Ihr Einkaufspreis bei ED STEEL (schreibgeschützt).</li>
          <li>• Der <strong>B2C-VK</strong> ist der Preis, den Ihre Endkunden zahlen (inkl. MwSt.).</li>
          <li>• Produkte werden nur angezeigt, wenn ein B2C-Preis gesetzt UND &quot;Sichtbar&quot; aktiviert ist.</li>
        </ul>
      </div>
    </div>
  );
};

// ============= TAB 2: OWN PRODUCTS =============
const OwnProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const fileInputRefs = useRef({});
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price_gross: '',
    category: '',
    sku: '',
    stock_quantity: 0,
    is_visible: true,
    is_featured: false,
  });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.all([
        api.get('/reseller/own-products'),
        api.get('/reseller/own-products/categories'),
      ]);
      setProducts(pRes.data || []);
      setCategories(cRes.data?.categories || []);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', description: '', price_gross: '', category: '',
      sku: '', stock_quantity: 0, is_visible: true, is_featured: false,
    });
    setEditing(null);
    setShowForm(false);
  };

  const startEdit = (product) => {
    setFormData({
      name: product.name || '',
      description: product.description || '',
      price_gross: (product.price_gross ?? '').toString().replace('.', ','),
      category: product.category || '',
      sku: product.sku || '',
      stock_quantity: product.stock_quantity || 0,
      is_visible: !!product.is_visible,
      is_featured: !!product.is_featured,
    });
    setEditing(product);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const priceNum = parseFloat((formData.price_gross || '').toString().replace(',', '.'));
    if (isNaN(priceNum) || priceNum <= 0) {
      toast.error('Bitte einen gültigen Preis eingeben');
      return;
    }
    const payload = {
      name: formData.name,
      description: formData.description || null,
      price_gross: priceNum,
      category: formData.category || null,
      sku: formData.sku || null,
      stock_quantity: parseInt(formData.stock_quantity) || 0,
      is_visible: formData.is_visible,
      is_featured: formData.is_featured,
    };
    try {
      if (editing) {
        await api.put(`/reseller/own-products/${editing.id}`, payload);
        toast.success('Produkt aktualisiert');
      } else {
        await api.post('/reseller/own-products', payload);
        toast.success('Produkt erstellt');
      }
      resetForm();
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Produkt wirklich löschen?')) return;
    try {
      await api.delete(`/reseller/own-products/${id}`);
      toast.success('Produkt gelöscht');
      loadAll();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleImageUpload = async (productId, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Nur Bilder erlaubt');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Datei zu groß (max. 5 MB)');
      return;
    }
    setUploadingImage(productId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/reseller/own-products/${productId}/image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Bild hochgeladen');
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploadingImage(null);
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '-';
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(price);
  };

  const filtered = products.filter(p =>
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.category || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="own-products-tab">
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="text-sm text-gray-600">
          Ihre eigenen Produkte (Wachs, Shampoo, Gutscheine ...). Diese erscheinen ausschließlich in Ihrem Endkundenshop.
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-own-product-btn">
          <Plus className="h-4 w-4 me-1" />
          Eigenes Produkt anlegen
        </Button>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Produkt suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="ps-10"
            data-testid="own-search-input"
          />
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-gray-50 border rounded-lg p-4 mb-6 space-y-4" data-testid="own-product-form">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">
              {editing ? 'Produkt bearbeiten' : 'Neues eigenes Produkt'}
            </h3>
            <button type="button" onClick={resetForm} className="p-1 hover:bg-gray-200 rounded">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="own-product-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Kategorie</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
                data-testid="own-product-category-select"
              >
                <option value="">— keine —</option>
                {categories.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Preis (Brutto €) *</label>
              <Input
                value={formData.price_gross}
                onChange={(e) => setFormData({ ...formData, price_gross: e.target.value.replace(/[^0-9,]/g, '') })}
                placeholder="19,90"
                required
                data-testid="own-product-price-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Art.-Nr. / SKU</label>
              <Input
                value={formData.sku}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                data-testid="own-product-sku-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Bestand</label>
              <Input
                type="number"
                min="0"
                value={formData.stock_quantity}
                onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                data-testid="own-product-stock-input"
              />
            </div>
            <div className="flex items-end gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formData.is_visible}
                  onChange={(e) => setFormData({ ...formData, is_visible: e.target.checked })}
                  data-testid="own-product-visible-checkbox"
                />
                Sichtbar
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  data-testid="own-product-featured-checkbox"
                />
                Hervorgehoben
              </label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Beschreibung</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full border rounded-lg px-3 py-2"
              data-testid="own-product-description-input"
            />
          </div>
          <div className="flex gap-2">
            <Button type="submit" data-testid="own-product-save-btn">
              <Save className="h-4 w-4 me-1" />
              {editing ? 'Aktualisieren' : 'Anlegen'}
            </Button>
            <Button type="button" variant="outline" onClick={resetForm}>Abbrechen</Button>
          </div>
        </form>
      )}

      {/* List */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-white border border-dashed border-gray-300 rounded-lg">
          <Package className="h-10 w-10 mx-auto text-gray-300 mb-2" />
          <p className="text-gray-500">
            {searchTerm ? 'Keine eigenen Produkte gefunden' : 'Noch keine eigenen Produkte angelegt'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(p => (
            <div
              key={p.id}
              className="bg-white border border-border rounded-lg p-4 flex flex-col"
              data-testid={`own-product-card-${p.id}`}
            >
              {/* Image */}
              <div className="relative group w-full h-40 bg-gray-100 rounded mb-3 flex items-center justify-center overflow-hidden">
                {p.image_url ? (
                  <img
                    src={resolveImageUrl(p.image_url)}
                    alt={p.name}
                    className="w-full h-full object-contain p-2 bg-white"
                    loading="lazy"
                  />
                ) : (
                  <Store className="h-10 w-10 text-gray-300" />
                )}
                <label
                  className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                  data-testid={`own-product-upload-image-${p.id}`}
                >
                  {uploadingImage === p.id ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <div className="text-white text-sm flex items-center gap-1">
                      <Upload className="h-4 w-4" /> Bild ändern
                    </div>
                  )}
                  <input
                    ref={el => { if (el) fileInputRefs.current[p.id] = el; }}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImageUpload(p.id, e.target.files?.[0])}
                  />
                </label>
              </div>
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold flex-1">{p.name}</h3>
                <div className="flex items-center gap-1">
                  {p.is_featured && <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />}
                  {p.is_visible ? (
                    <Eye className="h-4 w-4 text-green-600" title="Sichtbar" />
                  ) : (
                    <EyeOff className="h-4 w-4 text-gray-400" title="Verborgen" />
                  )}
                </div>
              </div>
              {p.category && (
                <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-primary/10 text-primary rounded self-start">
                  {p.category}
                </span>
              )}
              {p.description && (
                <p className="text-sm text-gray-500 mt-2 line-clamp-2">{p.description}</p>
              )}
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="font-bold text-primary">{formatPrice(p.price_gross)}</span>
                <span className="text-gray-500">Bestand: {p.stock_quantity ?? 0}</span>
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  onClick={() => startEdit(p)}
                  data-testid={`own-product-edit-${p.id}`}
                >
                  <Edit2 className="h-3 w-3 me-1" /> Bearbeiten
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => handleDelete(p.id)}
                  data-testid={`own-product-delete-${p.id}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============= MAIN PAGE =============
const MyShopProductsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('panasonic');

  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="my-shop-products-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/my-shop')} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 me-1" />
          Zurück
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="h-6 w-6" />
            Produkte &amp; Preise
          </h1>
          <p className="text-gray-600">
            Panasonic-Produkte und Ihre eigenen Produkte für den Endkundenshop.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b">
        <button
          onClick={() => setActiveTab('panasonic')}
          className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 -mb-px border-b-2 text-sm sm:text-base whitespace-nowrap transition-colors ${
            activeTab === 'panasonic'
              ? 'border-primary text-primary font-semibold'
              : 'border-transparent text-gray-500 hover:text-gray-800'
          }`}
          data-testid="tab-panasonic"
        >
          <span className="hidden sm:inline">Panasonic-Produkte</span>
          <span className="sm:hidden">Panasonic</span>
        </button>
        <button
          onClick={() => setActiveTab('own')}
          className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 -mb-px border-b-2 text-sm sm:text-base whitespace-nowrap transition-colors ${
            activeTab === 'own'
              ? 'border-primary text-primary font-semibold'
              : 'border-transparent text-gray-500 hover:text-gray-800'
          }`}
          data-testid="tab-own"
        >
          <span className="hidden sm:inline">Meine eigenen Produkte</span>
          <span className="sm:hidden">Eigene</span>
        </button>
      </div>

      {activeTab === 'panasonic' && <PanasonicProductsTab />}
      {activeTab === 'own' && <OwnProductsTab />}
    </div>
  );
};

export default MyShopProductsPage;
